<template>
  <v-data-table
    :headers="headers"
    :items="customSearch(employees, headers, search, ['email'])"
    :loading="loading"
    @current-items="$emit('current-items', $event)"
    sort-by="name"
    class="striped-table ordera-box-shadow"
    mobile-breakpoint="0"
    id="print"
  >
    <template v-slot:header.time_limit="{ header }">
      <div class="d-flex align-center justify-center">
        <span class="mr-2">{{ header.text }}</span>
        <v-checkbox
          v-if="company?.no_time_limit"
          :input-value="selectAllTimeLimit"
          @change="$emit('toggle-all-time-limit', $event)"
          color="primary"
          hide-details
          :true-value="1"
          :false-value="0"
          class="mt-2 pa-0 time-limit-checkbox"
        />
      </div>
    </template>

    <template v-slot:item.external_code="{ item }">
      <v-chip v-if="item.external_code" small outlined>
        {{ item.external_code }}
      </v-chip>
    </template>

    <template v-slot:item.company="{ item }">
      {{ toLatin(item.company?.name || "") }}
    </template>

    <template v-slot:item.name="{ item }">
      <div class="d-flex align-center">
        <v-avatar size="32" :color="getUserAvatarColor(item.type)" class="mr-2">
          <span class="white--text text-subtitle-2">
            {{ toLatin(getInitials(item.name, item.lastname)) }}
          </span>
        </v-avatar>
        <div>
          <div class="font-weight-medium">
            {{ toLatin(item.name) }} {{ toLatin(item.lastname) }}
          </div>
          <div class="text-caption grey--text">{{ item.email }}</div>
        </div>
      </div>
    </template>

    <template v-slot:item.lastname="{ item }">
      {{ toLatin(item.lastname) }}
    </template>

    <template v-slot:item.type="{ item }">
      <v-chip :color="getUserTypeColor(item.type)" small dark>
        <v-icon left x-small>{{ getUserTypeIcon(item.type) }}</v-icon>
        {{ getUserTypeLabel(item.type) }}
      </v-chip>
    </template>

    <template v-slot:item.state="{ item }">
      <v-chip :color="item.state ? 'success' : 'grey'" small outlined>
        <v-icon left x-small>
          {{ item.state ? "mdi-check-circle" : "mdi-cancel" }}
        </v-icon>
        <span v-if="item.state">{{ $t("active") }}</span>
        <span v-else>{{ $t("inactive") }}</span>
      </v-chip>
    </template>

    <template v-slot:item.menu="{ item, index }">
      <circle-button
        color="primary"
        icon="mdi-microsoft-xbox-controller-menu"
        @click="$emit('user-can-see-menu', item, index)"
        :tooltip="$t('tooltip_enable_menu_access')"
      />
    </template>

    <template v-slot:item.can_report="{ item, index }">
      <span class="d-flex justify-center">
        <v-checkbox
          v-if="item.type == 'company'"
          :title="$t('manage_worker_accounts_and_view_reports')"
          :input-value="item.can_report"
          :true-value="1"
          :false-value="0"
          @change="(value) => $emit('set-can-report', item, index, value)"
        />
      </span>
    </template>

    <template v-slot:item.credit="{ item, index }">
      {{ item.credit[0] ? item.credit[0].duguje : "" }}
    </template>

    <template v-slot:item.time_limit="{ item, index }">
      <span class="d-flex justify-center" v-if="item.company?.no_time_limit">
        <v-checkbox
          v-model="item.no_time_limit_user"
          :false-value="0"
          :true-value="1"
          @change="$emit('set-time-limit', item, index)"
        />
      </span>
    </template>

    <template v-slot:item.actions="{ item, index }">
      <v-row no-gutters justify="end">
        <qrcode-vue
          class="d-none"
          :id="`qrkodstampa${item.id}`"
          :value="
            JSON.stringify({
              id: item.id,
              secure: item.qrCode,
            })
          "
          :size="120"
          level="H"
          render-as="svg"
        />

        <circle-button
          v-if="item.type === 'table'"
          class="mx-2"
          @click="$emit('create-table-qr', item)"
          icon="mdi-qrcode"
          :tooltip="$t('tooltip_generate_table_qr')"
        />

        <circle-button
          v-if="!isDeliveryUsersContext"
          @click="$emit('openUserReport', { employee: item, company: item.company })"
          icon="mdi-clipboard-text-outline"
          :tooltip="$t('tooltip_employee_report')"
        />

        <circle-button
          class="mx-2"
          @click="$emit('show-employee-qr', item)"
          icon="mdi-qrcode-scan"
          :tooltip="$t('tooltip_show_employee_qr')"
        />

        <circle-button
          class="mr-2"
          @click="$emit('open-credit2', item)"
          :tooltip="$t('credit_transactions')"
          icon="mdi-credit-card-outline"
          v-if="!isDeliveryUsersContext && item.company?.contract === 4"
        />

        <edit-button class="mr-2" @click="$emit('edit-item', item)" />
        <remove-button @click="$emit('delete-item', item)" />
      </v-row>
    </template>
  </v-data-table>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import deliveryUsersContext from "@/mixins/deliveryUsersContext";

export default {
  name: "EmployeeTable",
  mixins: [deliveryUsersContext],
  components: {
    QrcodeVue,
  },
  props: {
    employees: {
      type: Array,
      required: true,
    },
    search: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      default: null,
    },
    selectAllTimeLimit: {
      type: Number,
      default: 0,
    },
    showCreditColumn: {
      type: Boolean,
      default: false,
    },
    showOfficeColumn: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    companyId() {
      return this.$route.params.id || this.$store.state.company.id;
    },
    headers() {
      const headers = [
        {
          text: "ID",
          align: "start",
          value: "id",
        },
        {
          width: "150px",
          text: this.$t("external_code"),
          align: "start",
          value: "external_code",
        },
        {
          text: this.$t("name"),
          align: "start",
          value: "name",
          sortable: true,
        },
      ];

      // Add office column when filtering by multiple companies
      if (this.showOfficeColumn) {
        headers.push({
          text: this.$t("firm"),
          align: "start",
          value: "company",
          sortable: true,
        });
      }

      headers.push(
        {
          text: this.$t("role"),
          align: "start",
          value: "type",
          sortable: true,
          width: "150px",
        },
        {
          text: this.$t("status"),
          align: "start",
          value: "state",
          sortable: true,
          width: "140px",
        }
      );

      // "Report workers" only ever applies to type === 'company' rows
      // (regular employees) — always empty for delivery users.
      if (!this.isDeliveryUsersContext) {
        headers.push({
          text: this.$t("report_workers"),
          align: "center",
          value: "can_report",
          sortable: true,
        });
      }

      headers.push({
        text: this.$t("time_limit"),
        align: "center",
        value: "time_limit",
        sortable: false,
      });

      if (this.user?.type !== "recipient") {
        headers.push({
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "end",
        });
      }

      // Add menu column for admin users with restaurant entity type
      if (
        this.user?.type === "admin" &&
        ["restaurant", "shop"].includes(this.company?.entity_type)
      ) {
        const typeIndex = headers.findIndex((h) => h.value === "type");
        if (typeIndex !== -1) {
          headers.splice(typeIndex + 1, 0, {
            text: this.$t("menu"),
            align: "start",
            value: "menu",
            sortable: false,
          });
        }
      }

      // Add credit column if needed
      if (this.showCreditColumn) {
        const actionsIndex = headers.findIndex((h) => h.value === "actions");
        const insertIndex = actionsIndex !== -1 ? actionsIndex : headers.length;
        headers.splice(insertIndex, 0, {
          text: this.$t("credit_only"),
          align: "start",
          value: "credit",
          sortable: true,
        });
      }

      return headers;
    },
    headersForPrint() {
      return this.headers.filter((header) => {
        return !["menu", "actions", "time_limit", "report_workers"].includes(
          header.value
        );
      });
    },
  },
  methods: {
    getInitials(name, lastname) {
      const firstName = name || "";
      const lastName = lastname || "";
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    },

    getUserAvatarColor(type) {
      const colorMap = {
        admin: "purple",
        customer: "teal",
        company: "indigo",
        restaurant: "blue",
        recipient: "orange",
        guest: "green",
        report: "cyan",
        report_guest: "lime",
        warehouse: "brown",
        dostava: "amber",
        admin_delivery: "deep-orange",
        table: "pink",
        worker_more_restaurants: "deep-purple",
      };
      return colorMap[type] || "grey";
    },

    getUserTypeColor(type) {
      const colorMap = {
        admin: "purple",
        customer: "teal",
        company: "indigo",
        restaurant: "blue",
        recipient: "orange",
        guest: "green",
        report: "cyan",
        report_guest: "lime",
        warehouse: "brown",
        dostava: "amber",
        admin_delivery: "deep-orange",
        table: "pink",
        worker_more_restaurants: "deep-purple",
      };
      return colorMap[type] || "grey";
    },

    getUserTypeIcon(type) {
      const iconMap = {
        admin: "mdi-shield-crown",
        customer: "mdi-account",
        company: "mdi-office-building",
        restaurant: "mdi-silverware-fork-knife",
        recipient: "mdi-truck-delivery",
        guest: "mdi-account-outline",
        report: "mdi-chart-line",
        report_guest: "mdi-chart-box",
        warehouse: "mdi-warehouse",
        dostava: "mdi-bike-fast",
        admin_delivery: "mdi-shield-account",
        table: "mdi-table-furniture",
        worker_more_restaurants: "mdi-account-multiple",
      };
      return iconMap[type] || "mdi-account";
    },

    getUserTypeLabel(type) {
      const typeMap = {
        customer: "worker",
        company: "firm",
        restaurant: "restaurant",
        recipient: "delivery",
        guest: "guest",
        report: "report",
        report_guest: "guest_report",
        warehouse: "stock",
        dostava: "delivery1",
        admin_delivery: "admin_delivery",
        table: "table",
        worker_more_restaurants: "worker_more_restaurants",
      };
      return this.$t(typeMap[type] || "delivery");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_variables.scss";

// State indicators
.state-active {
  color: darken($success-color, 20%);
}

.state-inactive {
  color: darken($error-color, 10%);
}

// Time limit checkbox
.time-limit-checkbox {
  flex: 0 0 auto;
}
</style>
